import React, { useState, useEffect } from 'react';
import { 
  PlayIcon, 
  PauseIcon, 
  ArrowLeftIcon, 
  ArrowRightIcon,
  BookmarkIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

interface NavigationPoint {
  type: 'message' | 'checkpoint';
  id: string;
  messageIndex: number;
  description: string;
  timestamp: Date;
}

interface TimelineNavigationProps {
  navigationPoints: NavigationPoint[];
  currentMessageIndex: number;
  onNavigateToPoint: (messageIndex: number) => void;
  onResumeFromPoint: (messageIndex: number) => void;
  isPlaying: boolean;
  onPlayPause: () => void;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

const TimelineNavigation: React.FC<TimelineNavigationProps> = ({
  navigationPoints,
  currentMessageIndex,
  onNavigateToPoint,
  onResumeFromPoint,
  isPlaying,
  onPlayPause,
  isOpen,
  onClose,
  className = ''
}) => {
  const [selectedPoint, setSelectedPoint] = useState<NavigationPoint | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Find the current position in the timeline
  const currentPoint = navigationPoints.find(point => 
    point.messageIndex <= currentMessageIndex
  ) || navigationPoints[0];

  const handlePointClick = (point: NavigationPoint) => {
    setSelectedPoint(point);
    setShowDetails(true);
  };

  const handleNavigate = () => {
    if (selectedPoint) {
      onNavigateToPoint(selectedPoint.messageIndex);
      setShowDetails(false);
    }
  };

  const handleResume = () => {
    if (selectedPoint) {
      onResumeFromPoint(selectedPoint.messageIndex);
      setShowDetails(false);
    }
  };

  const getPointIcon = (point: NavigationPoint) => {
    if (point.type === 'checkpoint') {
      return <BookmarkIcon className="h-4 w-4" />;
    }
    return <ChatBubbleLeftRightIcon className="h-4 w-4" />;
  };

  const getPointColor = (point: NavigationPoint, index: number) => {
    if (point.messageIndex <= currentMessageIndex) {
      return 'bg-blue-500 text-white';
    }
    if (point.type === 'checkpoint') {
      return 'bg-green-500 text-white';
    }
    return 'bg-gray-300 text-gray-600';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Conversation Timeline</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={onPlayPause}
                className="p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
              >
                {isPlaying ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
              </button>
              <span className="text-sm text-gray-500">
                Message {currentMessageIndex} of {navigationPoints[navigationPoints.length - 1]?.messageIndex || 0}
              </span>
              <button
                onClick={onClose}
                className="ml-4 p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

      {/* Timeline */}
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>
        
        {/* Timeline points */}
        <div className="space-y-4">
          {navigationPoints.map((point, index) => (
            <div key={point.id} className="relative flex items-start">
              {/* Timeline point */}
              <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 border-white shadow-sm ${getPointColor(point, index)}`}>
                {getPointIcon(point)}
              </div>
              
              {/* Point content */}
              <div className="ml-4 flex-1 min-w-0">
                <button
                  onClick={() => handlePointClick(point)}
                  className="text-left w-full p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {point.description}
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">
                        Message {point.messageIndex} • {new Date(point.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        point.type === 'checkpoint' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {point.type === 'checkpoint' ? 'Checkpoint' : 'Message'}
                      </span>
                      {point.messageIndex <= currentMessageIndex && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Visited
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => {
              const prevPoint = navigationPoints
                .filter(p => p.messageIndex < currentMessageIndex)
                .pop();
              if (prevPoint) {
                onNavigateToPoint(prevPoint.messageIndex);
              }
            }}
            disabled={!navigationPoints.some(p => p.messageIndex < currentMessageIndex)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Previous
          </button>
          
          <button
            onClick={() => {
              const nextPoint = navigationPoints
                .filter(p => p.messageIndex > currentMessageIndex)
                .shift();
              if (nextPoint) {
                onNavigateToPoint(nextPoint.messageIndex);
              }
            }}
            disabled={!navigationPoints.some(p => p.messageIndex > currentMessageIndex)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ArrowRightIcon className="h-4 w-4 ml-1" />
          </button>
        </div>

        <div className="text-sm text-gray-500">
          <ClockIcon className="h-4 w-4 inline mr-1" />
          {navigationPoints.length > 0 && (
            <>
              {Math.round((new Date().getTime() - new Date(navigationPoints[0].timestamp).getTime()) / (1000 * 60))} minutes elapsed
            </>
          )}
        </div>
      </div>

          {/* Point Details Modal */}
          {showDetails && selectedPoint && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-60">
              <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
                <div className="mt-3">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {selectedPoint.type === 'checkpoint' ? 'Checkpoint Details' : 'Message Point Details'}
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <p className="text-sm text-gray-900">{selectedPoint.description}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Message Index
                        </label>
                        <p className="text-sm text-gray-900">{selectedPoint.messageIndex}</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Timestamp
                        </label>
                        <p className="text-sm text-gray-900">
                          {new Date(selectedPoint.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        selectedPoint.type === 'checkpoint' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {selectedPoint.type === 'checkpoint' ? 'Checkpoint' : 'Message Point'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      onClick={() => setShowDetails(false)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleNavigate}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Navigate Here
                    </button>
                    <button
                      onClick={handleResume}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      Resume From Here
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimelineNavigation;
